/**
 * Board 3's rules, apart from the boxes that draw them — `docs/job-stages.md`
 * §8, so a test can read them without a browser.
 *
 * - **A · Approve**: who and when are always required; *When* is not in the
 *   future and not before the quote was sent; every choose-one group has an
 *   answer (*none* only where the group allows it); how, what was said and
 *   proof become required when the company says so (Board 3 C) — the server
 *   checks the same (`422 {field}`).
 * - **B · Mark quoted**: a quote is picked (the first draft starts picked).
 * - **D · Withdraw**, **F · Cancel job**: *Why* has text.
 * - **E · Mark declined**: one of the five reasons.
 */
import type { JobStagesQuoteRow, MarkedHow, OfficeApprovalHow, ProposalView } from '@rowted/shared'

/** What the company requires beyond who and when (`tenant_settings.approval_require_*`). */
export interface ApprovalRequires { how: boolean; note: boolean; proof: boolean }

export const NOTHING_REQUIRED: ApprovalRequires = { how: false, note: false, proof: false }

/** Board 3 A, as somebody has filled it in so far. */
export interface ApproveForm {
  /** The contact picked, when the property has contacts. */
  contactId: string | null
  /** The name typed, when it has none. */
  typedName: string
  /** Whether the property has contacts — the picker, or a typed name. */
  hasContacts: boolean
  /** *When*, as an instant, or null while it does not parse. */
  when: Date | null
  how: OfficeApprovalHow | null
  note: string
  proofFileId: string | null
  /** groupId → option id, null for *none*, absent while unanswered. */
  choose: Record<string, string | null>
}

/** Why Approve cannot be pressed yet — one entry per field, in the board's order. */
export type ApproveProblem =
  | 'who'
  | 'when'
  | 'when_future'
  | 'when_before_sent'
  | 'how'
  | 'choose'
  | 'note'
  | 'proof'

export function approveProblems(form: ApproveForm, context: {
  requires: ApprovalRequires
  groups: readonly { id: string; allowNone: boolean }[]
  now: Date
  /** When the quote was sent — *When* may not be before it. */
  sentAt: string | null
}): ApproveProblem[] {
  const problems: ApproveProblem[] = []
  if (form.hasContacts ? !form.contactId : form.typedName.trim() === '') problems.push('who')
  if (!form.when || Number.isNaN(form.when.getTime())) {
    problems.push('when')
  } else {
    const sent = context.sentAt ? new Date(context.sentAt) : null
    /*
     * A minute's grace: the box shows minutes, and *now* was a minute ago by
     * the time Approve is pressed. A browser whose clock is behind the
     * server's still has the moment it was sent to offer.
     */
    const latest = Math.max(context.now.getTime(), sent?.getTime() ?? 0)
    if (form.when.getTime() > latest + 60_000) problems.push('when_future')
    if (sent && floorMinute(form.when) < floorMinute(sent)) problems.push('when_before_sent')
  }
  if (context.requires.how && !form.how) problems.push('how')
  for (const group of context.groups) {
    const answer = form.choose[group.id]
    if (answer === undefined || (answer === null && !group.allowNone)) { problems.push('choose'); break }
  }
  if (context.requires.note && form.note.trim() === '') problems.push('note')
  if (context.requires.proof && !form.proofFileId) problems.push('proof')
  return problems
}

const floorMinute = (date: Date) => Math.floor(date.getTime() / 60_000)

/**
 * Who approved, to start with: the contact the proposal is prepared for
 * (`readProposal`'s pick — the property's primary contact, else the first one
 * linked), else the first contact, else nobody (a typed name).
 */
export function startingContact(
  contacts: readonly { id: string }[], preparedForContactId: string | null,
): string | null {
  if (preparedForContactId && contacts.some((one) => one.id === preparedForContactId)) return preparedForContactId
  return contacts[0]?.id ?? null
}

/**
 * *When* to start with: now, to the minute — but never before the quote was
 * sent (a clock behind the server's would otherwise start it invalid).
 */
export function startingWhen(now: Date, sentAt: string | null): Date {
  const minute = new Date(Math.floor(now.getTime() / 60_000) * 60_000)
  const sent = sentAt ? new Date(sentAt) : null
  return sent && sent.getTime() > minute.getTime() ? sent : minute
}

/** `<input type="datetime-local">`'s value for a moment, in this browser's zone. */
export function localInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** The moment an `<input type="datetime-local">` holds, or null. */
export function fromLocalInput(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/** Every choose-one group answered — the starting selection's answers kept, the rest left to pick. */
export function chooseOf(view: Pick<ProposalView, 'groups'>, start: Record<string, string | null>): Record<string, string | null> {
  const choose: Record<string, string | null> = {}
  for (const group of view.groups) {
    const picked = start[group.id]
    if (picked === null && group.allowNone) choose[group.id] = null
    else if (picked && group.options.some((one) => one.id === picked)) choose[group.id] = picked
  }
  return choose
}

// ---------------------------------------------------------------------------
// B · Mark quoted
// ---------------------------------------------------------------------------

/**
 * The radio rows: from the job, its drafts newest first (the highest version,
 * then the latest made); from a draft's ⋯, only that draft.
 */
export function markQuotedRows(quotes: readonly JobStagesQuoteRow[], quoteId?: string | null): JobStagesQuoteRow[] {
  if (quoteId) return quotes.filter((one) => one.id === quoteId)
  return quotes
    .filter((one) => one.status === 'draft')
    .sort((a, b) => b.version - a.version || b.createdAt.localeCompare(a.createdAt))
}

export interface MarkQuotedForm { quoteId: string | null; how: MarkedHow | null }

export const markQuotedReady = (form: MarkQuotedForm): boolean => form.quoteId !== null

// ---------------------------------------------------------------------------
// D · Withdraw, E · Mark declined, F · Cancel job
// ---------------------------------------------------------------------------

/** *Withdraw* and *Cancel job* wait for *Why* (Board 3 D: "disabled until *Why* has text"). */
export const hasWhy = (why: string): boolean => why.trim().length > 0

/** *Mark declined* waits for one of the five reasons. */
export const hasReason = (reason: string | null): boolean => reason !== null
